import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Pen,
    Trash2,
} from "lucide-react";
import { useSearch } from "../../SearchContext";
import { useSidebar } from "../../SidebarContext";
import Loader from "../Loader";
import Swal from "sweetalert2";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const dummyEmployees = [
    {
        id: 1,
        selfie: "https://plus.unsplash.com/premium_photo-1664536392779-049ba8fde933?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        name: "Amit Sharma",
        employeeId: "EMP001",
        checkIn: {
            dateTime: "2025-08-29T09:00:00",
            location: "Main Office, Delhi",
        },
        checkOut: {
            dateTime: "2025-08-29T17:00:00",
            location: "Main Office, Delhi",
        },
    },
    {
        id: 2,
        selfie: "https://plus.unsplash.com/premium_photo-1664536392779-049ba8fde933?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        name: "Priya Verma",
        employeeId: "EMP002",
        checkIn: {
            dateTime: "2025-08-29T08:45:00",
            location: "Branch Office, Mumbai",
        },
        checkOut: {
            dateTime: "2025-08-29T16:30:00",
            location: "Branch Office, Mumbai",
        },
    },
    {
        id: 3,
        selfie: null,
        name: "Rohan Gupta",
        employeeId: "EMP003",
        checkIn: {
            dateTime: "2025-08-29T09:15:00",
            location: "Remote, Bangalore",
        },
        checkOut: null,
    },
    {
        id: 4,
        selfie: "https://plus.unsplash.com/premium_photo-1664536392779-049ba8fde933?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        name: "Sneha Iyer",
        employeeId: "EMP004",
        checkIn: {
            dateTime: "2025-08-29T10:00:00",
            location: "Headquarters, Chennai",
        },
        checkOut: {
            dateTime: "2025-08-29T18:00:00",
            location: "Headquarters, Chennai",
        },
    },
    {
        id: 5,
        selfie: "https://plus.unsplash.com/premium_photo-1664536392779-049ba8fde933?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        name: "Vishal Jaggi",
        employeeId: "EMP005",
        checkIn: {
            dateTime: "2025-08-29T09:30:00",
            location: "Office, Hyderabad",
        },
        checkOut: {
            dateTime: "2025-08-29T17:30:00",
            location: "Office, Hyderabad",
        },
    },
];

const TeamAttendance = () => {
    const { isSidebarOpen } = useSidebar();
    const { globalSearch } = useSearch();
    const [currentPage, setCurrentPage] = useState(1);
    const [load, setLoad] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_LINK;
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || ""
    // Helper function to format date-time
    const safeDate = (dateTime) =>
        dateTime
            ? new Date(dateTime).toLocaleString("en-US", {
                dateStyle: "short",
                timeStyle: "short",
            })
            : "—";



    const fetchDataAttendance = async () => {
        try {
            const response = await fetch(`${apiUrl}/staffattendance`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            })
            const res = await response.json();
            console.log(res)
            setEmployees(res);
            setFilteredEmployees(res)
        } catch (error) {
            console.log(error)
        }
    }

    // 🔍 Filtering by search
    useEffect(() => {
        let filtered = [...employees];
        if (globalSearch) {
            filtered = filtered.filter(
                (employee) =>
                    employee.staff_name.toLowerCase().includes(globalSearch.toLowerCase()) ||
                    employee.staff_id
                        .toLowerCase()
                        .includes(globalSearch.toLowerCase()) ||
                    (employee.checkin_location &&
                        employee.checkin_location
                            .toLowerCase()
                            .includes(globalSearch.toLowerCase())) ||
                    (employee.checkout_location &&
                        employee.checkout_location
                            .toLowerCase()
                            .includes(globalSearch.toLowerCase()))
            );
        }
        setFilteredEmployees(filtered);
    }, [employees, globalSearch]);

    // Pagination
    const entriesPerPage = 5;
    const totalPages = Math.ceil(filteredEmployees.length / entriesPerPage);
    const currentData = filteredEmployees.slice(
        (currentPage - 1) * entriesPerPage,
        currentPage * entriesPerPage
    );

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleDelete = (employeeId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                setEmployees((prev) =>
                    prev.filter((item) => item.id !== employeeId)
                );
                setFilteredEmployees((prev) =>
                    prev.filter((item) => item.id !== employeeId)
                );
                toast.success("Employee has been deleted.");
            }
        });
    };



    useEffect(() => {
        fetchDataAttendance();
    }, [])

    if (load) {
        return <Loader />;
    }

    return (
        <div className={`open-close ${isSidebarOpen ? "open" : "closed"}`}>
            <div className="oms-header">
                <h1 className="dashboard-title">Team Attendance</h1>
            </div>

            {/* Employee Table */}
            <div className="customer-table responsive-table">
                <table>
                    <thead>
                        <tr>
                            <th>Selfie</th>
                            <th>Staff Name</th>
                            <th>ID</th>
                            <th>Check In</th>
                            <th>Check Out</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.length > 0 ? (
                            currentData.map((employee) => (
                                <tr key={employee.id}>
                                    <td>
                                        {employee.selfie ? (
                                            <img
                                                src={`${apiUrl}/${employee.selfie}`}
                                                alt="Selfie"
                                                style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }}
                                            />
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            style={{ cursor: "pointer" }}
                                            onClick={() => navigate(`/employee-profile/${employee.staff_id}/${employee.staff_name}`)}
                                        >
                                            {employee.staff_name || "N/A"}
                                        </span>
                                    </td>
                                    <td>{employee.staff_id || "N/A"}</td>
                                    <td>
                                        {employee.checkin_date
                                            ? `${safeDate(employee.checkin_date)} - ${employee.checkin_location || "N/A"
                                            }`
                                            : "—"}
                                    </td>
                                    <td>
                                        {employee.checkout_date
                                            ? `${safeDate(employee.checkout_date)} - ${employee.checkout_location || "N/A"
                                            }`
                                            : "—"}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="edit-btn"
                                                onClick={() => {
                                                    setSelectedEmployee(employee);
                                                    setIsModalOpen(true);
                                                }}
                                            >
                                                <Pen size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(employee.id)}
                                                className="delete-btn"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ textAlign: "center" }}>
                                    No Employees found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Responsive Cards */}
            <div className="ecom-responsive-cards">
                {currentData.length === 0 ? (
                    <p style={{ textAlign: "center", margin: "50px" }}>
                        No Employees Available
                    </p>
                ) : (
                    currentData.map((employee, index) => (
                        <div
                            className="ecom-responsive-card"
                            key={index}
                            onClick={() => navigate(`/employee-detail/${employee.id}`)}
                            style={{ cursor: "pointer" }}
                        >
                            <div className="ecom-responsive-header">
                                <p>{employee.staff_id || "—"}</p>
                                {employee.selfie ? (
                                    <img
                                        src={employee.selfie}
                                        alt="Selfie"
                                        style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }}
                                    />
                                ) : (
                                    <p>No Selfie</p>
                                )}
                            </div>
                            <div className="ecom-responsive-body">
                                <div className="ecom-responsive-left">
                                    <h2
                                        style={{ cursor: "pointer" }}
                                        onClick={() => navigate(`/employee-profile/${employee.id}`)}
                                    >{employee.staff_name || "No Name Provided"}</h2>
                                    <p>
                                        Check In:{" "}
                                        {employee.checkin_date
                                            ? `${safeDate(employee.checkin_date)} - ${employee.checkin_location || "N/A"
                                            }`
                                            : "—"}
                                    </p>
                                </div>
                                <div className="ecom-responsive-right">
                                    <p>
                                        Check Out:{" "}
                                        {employee.checkout_date
                                            ? `${safeDate(employee.checkout_date)} - ${employee.checkout_location || "N/A"
                                            }`
                                            : "—"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronsLeft size={18} />
                    </button>
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button className="pagination-btn active">{currentPage}</button>
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronRight size={18} />
                    </button>
                    <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronsRight size={18} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default TeamAttendance;